/**
 * UserReportPreferences — iter868.
 *
 * Reusable component for the User edit dialog "Reports & Alerts" tab.
 * Renders a matrix of report_type × channel (Email / WhatsApp) as
 * checkboxes and saves via PUT /users/{id}/report-preferences.
 *
 * Usage:
 *   <UserReportPreferences userId={selectedUser.id} />
 */
import { useEffect, useState } from 'react';
import { Bell, Mail, MessageCircle, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

const REPORT_LABELS = {
  sales: 'Sales Summary',
  sales_by_branch: 'Daily Branch Sales',
  expenses: 'Expenses Summary',
  pnl: 'Profit & Loss',
  supplier_aging: 'Supplier Aging',
  inventory_low_stock: 'Low Stock alerts',
  payroll_totals: 'Payroll Totals',
  cash_flow: 'Cash Flow / Bank Balances',
  top_selling_items: 'Top Selling Items',
};

export default function UserReportPreferences({ userId }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  // iter873a — Preview my subscriptions
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!userId) return;
    api.get(`/users/${userId}/report-preferences`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load subscription preferences'));
  }, [userId]);

  if (!data) return <div className="p-4 text-sm text-muted-foreground">Loading…</div>;

  const setPref = (rtype, channel, value) => {
    const next = { ...(data.report_preferences || {}) };
    next[rtype] = { ...(next[rtype] || {}), [channel]: value };
    setData({ ...data, report_preferences: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${userId}/report-preferences`, { report_preferences: data.report_preferences });
      toast.success('Preferences saved. This user will now receive selected reports on the chosen channel(s).');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      // Save current selections first so the preview reflects unsaved changes.
      await api.put(`/users/${userId}/report-preferences`, { report_preferences: data.report_preferences });
      const r = await api.get(`/users/${userId}/report-preferences/preview`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const types = data.available_report_types || Object.keys(REPORT_LABELS);
  const noPhone = !data.phone;

  return (
    <div className="space-y-4" data-testid="user-report-prefs">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Bell size={14} className="text-primary" />
        Pick which scheduled reports this user should receive and on which channel. The daily/weekly/monthly cadence is set per-report in <a href="/scheduled-reports" className="underline">Scheduled Reports</a>.
      </div>
      {noPhone && (
        <div className="p-2 rounded bg-warning/10 text-warning text-[11px] border border-warning/20">
          WhatsApp channel is disabled — this user has no phone number. Add a phone to enable it.
        </div>
      )}
      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr>
              <th className="text-left py-2 px-3 font-medium">Report</th>
              <th className="py-2 px-3 font-medium w-24"><span className="inline-flex items-center gap-1"><Mail size={14} /> Email</span></th>
              <th className="py-2 px-3 font-medium w-24"><span className="inline-flex items-center gap-1"><MessageCircle size={14} /> WhatsApp</span></th>
            </tr>
          </thead>
          <tbody>
            {types.map(rt => {
              const cur = (data.report_preferences && data.report_preferences[rt]) || {};
              return (
                <tr key={rt} className="border-t" data-testid={`prefs-row-${rt}`}>
                  <td className="py-2 px-3">{REPORT_LABELS[rt] || rt}</td>
                  <td className="py-2 px-3 text-center">
                    <input
                      type="checkbox"
                      checked={!!cur.email}
                      disabled={!data.email}
                      onChange={e => setPref(rt, 'email', e.target.checked)}
                      data-testid={`prefs-${rt}-email`}
                      title={data.email ? '' : 'User has no email address'}
                    />
                  </td>
                  <td className="py-2 px-3 text-center">
                    <input
                      type="checkbox"
                      checked={!!cur.whatsapp}
                      disabled={noPhone}
                      onChange={e => setPref(rt, 'whatsapp', e.target.checked)}
                      data-testid={`prefs-${rt}-whatsapp`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={save} disabled={saving} data-testid="prefs-save-btn">
          {saving ? 'Saving…' : 'Save preferences'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={openPreview}
          disabled={previewLoading}
          data-testid="prefs-preview-btn"
        >
          {previewLoading
            ? <Loader2 size={14} className="animate-spin mr-1" />
            : <Eye size={14} className="mr-1" />}
          Preview upcoming sends
        </Button>
      </div>

      {/* iter873a — Preview my subscriptions dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-lg" data-testid="prefs-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye size={16} /> Upcoming subscription sends
            </DialogTitle>
          </DialogHeader>
          {previewLoading || !preview ? (
            <div className="py-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 size={14} className="animate-spin" /> Computing next 7 days…
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Badge variant="outline" className="border-info text-info" data-testid="prefs-preview-email-total">
                  <Mail size={12} className="mr-1" /> {preview.totals?.email || 0} emails
                </Badge>
                <Badge variant="outline" className="border-emerald-400 text-emerald-700" data-testid="prefs-preview-whatsapp-total">
                  <MessageCircle size={12} className="mr-1" /> {preview.totals?.whatsapp || 0} WhatsApp
                </Badge>
                <span className="text-xs text-muted-foreground ml-auto">in the next {preview.window_days} days</span>
              </div>
              {(preview.upcoming?.length || 0) === 0 ? (
                <div className="p-4 rounded-lg bg-muted text-sm text-muted-foreground text-center" data-testid="prefs-preview-empty">
                  No upcoming sends. Tick a checkbox above and click <b>Preview</b> again — or check that your admin has activated the corresponding scheduled report at <a href="/scheduled-reports" className="underline">Scheduled Reports</a>.
                </div>
              ) : (
                <div className="border rounded-lg divide-y max-h-80 overflow-y-auto" data-testid="prefs-preview-list">
                  {preview.upcoming.map((row) => (
                    <div key={row.report_type} className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">{row.report_name}</div>
                        <div className="flex gap-1">
                          {row.channels.map((c) => (
                            <Badge key={c} variant="secondary" className="text-[10px]">
                              {c === 'email' ? <Mail size={10} className="mr-0.5" /> : <MessageCircle size={10} className="mr-0.5" />}
                              {c}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1">
                        {row.schedule || 'one-off'} · {row.projected_sends.length} send{row.projected_sends.length === 1 ? '' : 's'}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1 font-mono truncate" title={row.projected_sends.join(', ')}>
                        Next: {new Date(row.projected_sends[0]).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {row.projected_sends.length > 1 && ` … +${row.projected_sends.length - 1} more`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
